import React from 'react';

import classNames from 'classnames';

import { STATUS_LABELS } from '../../StatusPill';
import TrendArrow from '../../TrendArrow';
import {
    box,
    captionHeight,
    CELL_PADDING,
    CONTENT_WIDTH,
    HEADER_ROW_HEIGHT,
    MARGIN_X,
    pt,
    ROW_HEIGHT,
    STATUS_MARKER_SIZE,
    TABLE_FONT_SIZE,
    TABLE_LINE_HEIGHTS,
} from '../slideGeometry';

import BlockCaption from './BlockCaption';

const ALIGN_CLASSES = {
    start: 'tw-text-start',
    center: 'tw-text-center',
    end: 'tw-text-end',
};

const STATUS_CLASSES = {
    R: 'tw-text-red',
    A: 'tw-text-amber-400',
    G: 'tw-text-green',
};

const TONE_CLASSES = {
    negative: 'tw-text-red',
    positive: 'tw-text-green',
};

/**
 * A status cell, in the column keyed `status` (or the block's `statusKey`), as the .pptx draws it: the
 * status as a word in its colour, the trend against the movement basis pack, then "manual" small and
 * grey when it was set by hand. A status that is not configured reads "–", or the cell's own text, in
 * regular weight.
 */
const StatusCell = ({ text, status }) => {
    if (status.value === 'not_configured') {
        return <span className="tw-font-normal">{text && text !== 'not_configured' ? text : '–'}</span>;
    }

    return (
        <span>
            <span className={classNames('tw-font-bold', STATUS_CLASSES[status.value])}>{STATUS_LABELS[status.value]?.() ?? status.value}</span>
            {status.trend && (
                <>
                    {' '}
                    <TrendArrow trend={status.trend} />
                </>
            )}
            {status.manual && (
                <span className="tw-text-charcoal-60" style={{ fontSize: pt(STATUS_MARKER_SIZE) }} title={$t('Set manually')}>
                    {' '}
                    {$t('manual')}
                </span>
            )}
        </span>
    );
};

/**
 * A table at its slide position, as the .pptx draws it, from the measures the Paginator took: the column
 * widths, the header's and each row's height and the size of any text below 9 pt. Text is never cut: it
 * wraps within its cell. A table from a columns block carries its own `x` and `width`; a caption sits above
 * it; a cell with a tone is drawn in red or green. A row's `cellStatus` colours a cell's text by rating, and
 * its `cellFlags` tint a cell amber.
 */
const TableBlock = ({ block }) => {
    const columns = block.columns || [];

    if (!columns.length) {
        return null;
    }

    const x = block.x ?? MARGIN_X;
    const width = block.width ?? CONTENT_WIDTH;
    const widths = block.columnWidths || [];
    const rows = block.rows || [];
    const headerHeight = block.headerHeight ?? HEADER_ROW_HEIGHT;
    const rowHeights = rows.map((row, index) => block.rowHeights?.[index] ?? ROW_HEIGHT);
    const tableHeight = rowHeights.reduce((sum, height) => sum + height, headerHeight);
    const captionOffset = captionHeight(block);
    const statusKey = block.statusKey ?? 'status';

    const cellStyle = (index, height, size = TABLE_FONT_SIZE) => ({
        width: widths[index],
        height,
        padding: CELL_PADDING,
        fontSize: pt(size),
        lineHeight: `${TABLE_LINE_HEIGHTS[size]}px`,
    });

    return (
        <>
            <BlockCaption block={block} />
            <table className="tw-border-collapse tw-table-fixed" style={{ ...box(x, block.y + captionOffset, width, tableHeight) }}>
                <thead>
                    <tr className="tw-bg-charcoal tw-text-white">
                        {columns.map((column, index) => (
                            <th
                                key={column.key}
                                scope="col"
                                className={classNames(
                                    'tw-font-bold tw-border tw-border-charcoal-15 tw-whitespace-normal tw-break-words',
                                    ALIGN_CLASSES[column.align] || 'tw-text-start',
                                )}
                                style={cellStyle(index, headerHeight, block.headerSizes?.[column.key])}
                            >
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className={row.isParent ? 'tw-bg-blue-10 tw-font-bold' : 'tw-bg-white'}>
                            {columns.map((column, index) => {
                                const text = row.cells?.[column.key] ?? '';
                                const isStatus = column.key === statusKey && !!row.status?.value;
                                const tone = TONE_CLASSES[row.tones?.[column.key]];
                                const cellStatusClass = !isStatus && STATUS_CLASSES[row.cellStatus?.[column.key]];
                                const isFlagged = !!row.cellFlags?.[column.key];
                                const cellTitle = row.titles?.[column.key];

                                let content = text;

                                if (isStatus) {
                                    content = <StatusCell text={text} status={row.status} />;
                                } else if (cellStatusClass) {
                                    content = <span className={classNames('tw-font-bold', cellStatusClass)}>{text}</span>;
                                } else if (cellTitle) {
                                    content = (
                                        <>
                                            <span aria-hidden="true">{text}</span>
                                            <span className="tw-sr-only">{cellTitle}</span>
                                        </>
                                    );
                                }

                                return (
                                    <td
                                        key={column.key}
                                        className={classNames(
                                            'tw-border tw-border-charcoal-15 tw-whitespace-normal tw-break-words',
                                            ALIGN_CLASSES[column.align] || 'tw-text-start',
                                            isFlagged && !isStatus && !cellStatusClass
                                                ? 'tw-bg-amber-100 tw-text-amber-800 tw-font-bold'
                                                : tone || 'tw-text-charcoal',
                                        )}
                                        style={cellStyle(index, rowHeights[rowIndex], block.cellSizes?.[rowIndex]?.[column.key])}
                                        title={cellTitle}
                                    >
                                        {content}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

export default TableBlock;
